import makeRequest from "./makeRequest";
import getRequest from "../utils/requests";
import getWindow from "../utils/window";
import { getAuthorisationHeader, getCustomerId } from "../utils/identity";
import { GET, POST } from "../constants";
import getConfig from "../config";
import type {
  MySizeProfile,
  BrandsResponse,
  RecommendedBrandSize,
  SizeGuideRecommendation,
} from "./mySizeProfileApiTypes";

const getSiteOrigin = () =>
  new URLSearchParams(getWindow()?.location?.search ?? "").get("x-site-origin");

const appendSiteOrigin = (queryParams: URLSearchParams) => {
  const siteOrigin = getSiteOrigin();

  if (siteOrigin) {
    queryParams.append("x-site-origin", siteOrigin);
  }
};

const getSizeProfileUrl = (customerId: string) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };
  const queryParams = new URLSearchParams();
  appendSiteOrigin(queryParams);

  const queryString = queryParams.toString();

  return `${sizeProfileApiUrl}/${encodeURIComponent(customerId)}${
    queryString ? `?${queryString}` : ""
  }`;
};

const getCustomerSizeGuideUrl = (
  customerId: string,
  productId: number,
  language?: string,
) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };

  const queryParams = new URLSearchParams({
    productId: productId.toString(),
    ...(language ? { language } : {}),
  });
  appendSiteOrigin(queryParams);

  return `${sizeProfileApiUrl}/${encodeURIComponent(
    customerId,
  )}/SizeGuide?${queryParams.toString()}`;
};

const getProductHasMappingUrl = (productId: number) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };
  const normalizedSizeProfileApiUrl = sizeProfileApiUrl.replace(/\/$/, "");
  const sizeProfileBaseUrl = normalizedSizeProfileApiUrl.endsWith(
    "/SizeProfile",
  )
    ? normalizedSizeProfileApiUrl
    : `${normalizedSizeProfileApiUrl}/SizeProfile`;
  const queryParams = new URLSearchParams({
    productId: productId.toString(),
  });
  appendSiteOrigin(queryParams);

  return `${sizeProfileBaseUrl}/ProductHasMapping?${queryParams.toString()}`;
};

const getApiTimeout = () => {
  const { sizeProfileApiTimeout } = getConfig() as {
    sizeProfileApiTimeout: number;
  };
  return sizeProfileApiTimeout;
};

const getBrandsUrl = (
  sizeSchema: string,
  customerId: string,
  language?: string,
) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };

  const queryParams = new URLSearchParams({
    sizeSchema,
    customerId,
    ...(language ? { language } : {}),
  });
  appendSiteOrigin(queryParams);

  return `${sizeProfileApiUrl}/Brands?${queryParams.toString()}`;
};

const getResolvedCustomerId = async (customerId?: string) =>
  customerId ?? getCustomerId();

const getApiRequestConfig = async () => {
  return {
    timeout: getApiTimeout(),
    headers: {
      authorization: await getAuthorisationHeader(),
    },
  };
};

type BackendSizeGuideResponse = {
  sizeProfileId?: string | null;
  sizeProfile?: string | null;
  brandSizes?: RecommendedBrandSize[] | null;
  sizeGuideMessage?: string | null;
};

type ProductHasMappingResponse = {
  hasMapping?: boolean;
};

const normalizeProfileResponse = (
  profile: MySizeProfile | null,
): MySizeProfile | null => {
  if (!profile) {
    return null;
  }

  if (profile.sizeProfileId == null && profile.brandId == null) {
    return null;
  }

  return {
    ...profile,
    sizeProfileId: profile.sizeProfileId ?? null,
    legLength: profile.legLength ?? null,
  };
};

const normalizeSizeGuideResponse = (
  response: BackendSizeGuideResponse | null,
): SizeGuideRecommendation | null => {
  if (!response) {
    return null;
  }

  return {
    sizeProfileId: response.sizeProfileId ?? response.sizeProfile ?? null,
    brandSizes: response.brandSizes ?? null,
    sizeGuideMessage: response.sizeGuideMessage ?? null,
  };
};

export const fetchBrands = async (
  sizeSchema: string,
  language?: string,
): Promise<BrandsResponse> => {
  const customerId = await getResolvedCustomerId();
  const config = await getApiRequestConfig();
  const response = await getRequest()[GET]({
    url: getBrandsUrl(sizeSchema, customerId, language),
    ...config,
    additionalLoggingParams: {
      requestType: "MY_SIZE_PROFILE_API",
    },
  });

  return response.data as BrandsResponse;
};

export const fetchProductHasMapping = async (
  productId: number,
): Promise<boolean> => {
  const response = await getRequest()[GET]({
    url: getProductHasMappingUrl(productId),
    timeout: getApiTimeout(),
    additionalLoggingParams: {
      requestType: "MY_SIZE_PROFILE_API",
    },
  });

  return Boolean(
    (response.data as ProductHasMappingResponse | null)?.hasMapping,
  );
};

export const fetchMySizeProfile = async (
  customerId?: string,
): Promise<MySizeProfile | null> => {
  const resolvedCustomerId = await getResolvedCustomerId(customerId);
  const config = await getApiRequestConfig();

  try {
    const response = await getRequest()[GET]({
      url: getSizeProfileUrl(resolvedCustomerId),
      ...config,
      additionalLoggingParams: {
        requestType: "MY_SIZE_PROFILE_API",
      },
    });
    return normalizeProfileResponse(response.data as MySizeProfile | null);
  } catch (error: unknown) {
    const httpError = error as Error & { response?: { status?: number } };
    if (httpError?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export async function saveMySizeProfile(
  customerId: string,
  sizeProfileId: string,
  legLength: number | null,
  brandId: number,
  brandSize: string,
): Promise<MySizeProfile> {
  const { timeout, headers } = await getApiRequestConfig();

  return makeRequest({
    params: undefined,
    url: getSizeProfileUrl(customerId),
    timeout,
    headers: {
      authorization: headers.authorization,
      "Content-Type": "application/json",
    },
    requestType: "MY_SIZE_PROFILE_API",
    apiName: "MySizeProfile",
    caller: "saveMySizeProfile",
    mapData: undefined,
    body: {
      sizeProfileId,
      legLength,
      brandId,
      brandSize,
    },
    handleErrorReturnValue: (error: Error) => {
      throw error;
    },
    method: POST,
  });
}

export const fetchCustomerSizeGuide = async (
  customerId: string,
  productId: number,
  language?: string,
): Promise<SizeGuideRecommendation | null> => {
  const config = await getApiRequestConfig();

  try {
    const response = await getRequest()[GET]({
      url: getCustomerSizeGuideUrl(customerId, productId, language),
      ...config,
      additionalLoggingParams: {
        requestType: "MY_SIZE_PROFILE_API",
      },
    });

    return normalizeSizeGuideResponse(
      response.data as BackendSizeGuideResponse | null,
    );
  } catch (error: unknown) {
    const httpError = error as Error & { response?: { status?: number } };

    if (httpError?.response?.status === 404) {
      return null;
    }

    throw error;
  }
};
